// ============================================================================
// « PROMOTIONS & SOLDES » — onglet 1, les promotions (maquette `isPromos`, relevé 2026-08-26).
//
// ⚠️ L'ORDRE EST UNE DONNÉE, PAS UN CONFORT. La maquette le pose en tête : « la caisse applique LA
// PREMIÈRE RÈGLE QUI CORRESPOND ». `core-pricing.applyPromotions` trie par priorité décroissante et
// verrouille chaque ligne sur la première promotion qui la remise. Glisser une carte change donc le
// prix encaissé — d'où une écriture serveur immédiate, jamais un réglage d'écran.
//
// ⚠️ « N ARTICLES CONCERNÉS » VIENT DU SERVEUR. La maquette le calcule en mémoire parce qu'elle tient
// tout le catalogue ; le back-office non. Le déduire d'ici donnerait un nombre plausible et faux —
// or c'est celui-là qui fait choisir une cible (même leçon que le comptage des articles rapides).
//
// ⚠️ LES RÈGLES S'APPLIQUENT RÉELLEMENT EN CAISSE depuis le 2026-08-26 (`sale/priced-sale`). Le
// bandeau qui disait le contraire a été retiré DANS LA MÊME PASSE que le branchement — jamais avant.
//
// ⚠️ L'ONGLET SOLDES EST RÉEL depuis le 2026-08-29 (promo-soldes.tsx) : les démarques se
// matérialisent en promotions marquées type='solde' — même canal, même moteur en caisse. Cet onglet
// n'est qu'un éditeur spécialisé au-dessus du même stockage : les démarques apparaissent AUSSI dans
// la liste des promotions, et c'est voulu (une seule vérité).
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { confirm, EmptyState, Icon, Modal, Picker, showToast, Trans, useTileDrag } from '@orgazone/ui-web';
import { currentCurrency, formatMoney, parseMinor, toInputValue } from '@orgazone/money-format';
import { formatRateBp, parseRateBp } from '@orgazone/core-vat';
import { api, type ArticleFacet, type ArticleFacets, type PromotionRow, type PromotionSave } from '../api';
import { SoldesTab } from './promo-soldes';
import { facetLabel, fieldLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';

/**
 * Les cinq ciblages offerts à la création, dans l'ordre des pastilles — le MÊME vocabulaire que le
 * serveur (`PROMO_DIMS`). La marque d'abord : c'est l'entité opérationnelle, le fournisseur n'est
 * qu'un groupe générique.
 */
export const DIMS = ['marq', 'fam', 'sais', 'sect', 'sup'] as const;
/** Les dimensions LUES seulement (import/reprise) — la liste sait les nommer, jamais les proposer. */
export const READ_ONLY_DIMS = ['sfam', 'coul', 'gencode', 'code'] as const;
export type TargetDim = (typeof DIMS)[number] | (typeof READ_ONLY_DIMS)[number];
const isTargetDim = (dim: string): dim is TargetDim =>
  (DIMS as readonly string[]).includes(dim) || (READ_ONLY_DIMS as readonly string[]).includes(dim);

/**
 * Le mot d'une dimension de ciblage — celui du MAGASIN pour les champs du catalogue.
 * ⚠️ UNE FONCTION, PAS UNE TABLE DE MODULE : `fieldLabel` se lit au rendu. Une table remplie au
 *    chargement du fichier figerait « Saison » avant que le contexte magasin ne soit lu.
 */
export function dimLabel(dim: TargetDim): string {
  switch (dim) {
    // Le fournisseur est un GROUPE de marques pour le ciblage : le mot du magasin, qualifié.
    case 'sup': return t('{v} (groupe)', { v: facetLabel('sup') });
    case 'gencode': return fieldLabel('gencode');
    case 'code': return t('Article');
    default: return facetLabel(dim);
  }
}

/** Les trois formes de remise — vocabulaire de `core-pricing.PromoForm`. */
const FORMS = [
  { k: 'pct', l: key('Pourcentage') },
  { k: 'amount', l: key('Euros') },
  { k: 'xy', l: key('X achetés, Y remisés') },
] as const;

/** Les trois modes de cumul — vocabulaire de `core-pricing.PromoCombineMode`. */
const COMBINE_MODES = [
  { k: 'best', l: key('Au plus favorable') },
  { k: 'stack', l: key('Cumulable') },
  { k: 'none', l: key('Non cumulable') },
] as const;
const COMBINE_LABEL: Record<string, Translatable> = Object.fromEntries(COMBINE_MODES.map((c) => [c.k, c.l]));

/** L'état d'une promotion à un instant — DÉRIVÉ des dates et de l'interrupteur, jamais stocké. */
type PromoState = 'off' | 'soon' | 'live' | 'done';
function stateOf(p: PromotionRow, now: number): PromoState {
  if (!p.isActive) return 'off';
  if (p.startsAt != null && now < p.startsAt) return 'soon';
  if (p.endsAt != null && now > p.endsAt) return 'done';
  return 'live';
}
const STATE_LABEL: Record<PromoState, Translatable> = { off: key('Désactivée'), soon: key('Planifiée'), live: key('En cours'), done: key('Terminée') };
const STATE_TONE: Record<PromoState, { bg: string; fg: string }> = {
  live: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  soon: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  done: { bg: 'var(--ink-100)', fg: 'var(--ink-500)' },
  off: { bg: 'var(--ink-100)', fg: 'var(--ink-500)' },
};

/** « −20 % » · « −5,00 € » · « 3 achetés → 1 à −50 % » — l'unité SUIT la forme, jamais l'inverse. */
function readableValue(p: PromotionRow): string {
  if (p.form === 'amount') return `−${formatMoney(p.value, { thousandsSep: ' ' })}`;
  const pct = (p.value / 100).toString().replace('.', ',');
  if (p.form === 'xy') return t('{achetes} → {discountQty} à −{pct} %', { achetes: t('{n} acheté', { n: p.buyQty }), discountQty: p.discountQty, pct });
  return `−${pct} %`;
}

/** `AAAA-MM-JJ` ↔ epoch ms, en heure LOCALE — un décalage UTC ferait glisser une promotion d'un jour. */
const toIsoDate = (ms: number | null): string => {
  if (ms == null) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** Début de journée pour le DÉBUT, fin de journée pour la FIN : une promotion « au 30 » couvre le 30. */
function fromIsoDate(iso: string, bound: 'debut' | 'fin'): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [a, mo, j] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return bound === 'debut' ? new Date(a, mo - 1, j, 0, 0, 0, 0).getTime() : new Date(a, mo - 1, j, 23, 59, 59, 999).getTime();
}
const toFrenchDate = (ms: number | null): string => (ms == null ? '—' : new Date(ms).toLocaleDateString('fr-FR'));

/** Le brouillon du formulaire — texte tant qu'il est saisi, converti à l'envoi seulement. */
interface Draft {
  id?: string;
  title: string;
  form: string;
  value: string;
  buyQty: string;
  discountQty: string;
  from: string;
  to: string;
  combineMode: string;
  targetDim: string;
  targetValue: string;
}
const BLANK_DRAFT: Draft = {
  title: '', form: 'pct', value: '20', buyQty: '3', discountQty: '1',
  from: '', to: '', combineMode: 'best', targetDim: 'fam', targetValue: '',
};

export function Promotions() {
  const [rows, setRows] = useState<PromotionRow[] | null>(null);
  const [tab, setTab] = useState<'promos' | 'soldes'>('promos');
  const [failure, setFailure] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [form, setForm] = useState<Draft | null>(null);
  const now = Date.now();

  const reload = useCallback(() => {
    api.promotions()
      .then((r) => { setRows(r.rows); setFailure(null); })
      // ⚠️ ON DIT LE MOTIF RÉEL et on NE VIDE PAS la liste : une liste vide affirmerait « aucune
      //    promotion », c'est-à-dire exactement le contraire de « je n'ai pas pu lire ».
      .catch((e) => setFailure(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(reload, [reload]);

  /** Une écriture : occupe l'écran, dit le motif RÉEL en cas d'échec, relit toujours. */
  const agir = useCallback(async (run: () => Promise<string | null>) => {
    if (occupe) return;
    setOccupe(true);
    try {
      const message = await run();
      if (message) showToast(message, 'ok');
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err', 12000);
    } finally {
      setOccupe(false);
      reload();
    }
  }, [occupe, reload]);

  const list = rows ?? [];
  const drag = useTileDrag({
    keys: list.map((p) => p.id),
    axis: 'y',
    disabled: occupe || form !== null,
    ignoreSelector: '[data-promo-action]',
    onReorder: (next) => {
      void agir(async () => {
        await api.reorderPromotions([...next], `promo:order:${Date.now()}`);
        return t('Ordre enregistré — la caisse le recevra à sa prochaine synchro.');
      });
    },
  });
  const byId = new Map(list.map((p) => [p.id, p]));

  const liveCount = list.filter((p) => stateOf(p, now) === 'live').length;
  const plannedCount = list.filter((p) => stateOf(p, now) === 'soon').length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            
            {t('Promotions & soldes')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='{live} en cours, {planifiees} — la caisse applique <1>la première règle qui correspond</1>.'
              vars={{ live: liveCount, planifiees: t('{n} planifiée', { n: plannedCount }) }}
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
      </div>

      {/* Deux onglets RÉELS — les soldes se matérialisent en promotions (mêmes canaux, même moteur
          en caisse) : leur onglet est un éditeur spécialisé, pas une seconde mécanique.
          ⚠️ SOULIGNÉS, comme la maquette (et comme Prix & marges) — la 1ʳᵉ version portait des
          pilules, un motif d'onglet qui n'existe nulle part dans la maquette du BO. */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--ink-200)', marginBottom: 20, overflowX: 'auto' }}>
        {([['promos', t('Promotions')], ['soldes', t('Soldes')]] as const).map(([id, label]) => (
          <button aria-pressed={tab === id}
            key={id} type="button" onClick={() => setTab(id)}
            style={{
              height: 40, padding: '0 15px', border: 'none', background: 'transparent', cursor: 'pointer',
              borderBottom: `2px solid ${tab === id ? 'var(--teal-500)' : 'transparent'}`,
              color: tab === id ? 'var(--ink-900)' : 'var(--ink-500)',
              fontSize: 'var(--t-title)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >{label}</button>
        ))}
      </div>

      {tab === 'soldes' && <SoldesTab />}

      {tab === 'promos' && (<>
      {failure && (
        <div style={{
          marginBottom: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)',
          background: 'var(--rose-bg)', border: '1px solid var(--rose)', fontSize: '12.5px', color: 'var(--ink-800)',
        }}>
          {t('Les promotions n’ont pas pu être lues : {failure}', { failure })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {/* Le libellé de la maquette : mono, majuscules, espacé — un repère de colonne, pas une phrase. */}
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
          color: 'var(--ink-500)', flex: 1, minWidth: 150,
        }}>{t('Par ordre de priorité')}</span>
        <button
          type="button" onClick={() => setForm({ ...BLANK_DRAFT })}
          style={{
            height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
            background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-title)', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            boxShadow: '0 6px 18px -8px #2bc9a3', flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          <Icon name="plus" size={16} />  {t('Nouvelle promotion')}
        </button>
      </div>

      {rows !== null && list.length === 0 && (
        <EmptyState
          icon="percent"
          title={t('Aucune promotion')}
          note={t('Créez-en une pour cibler une famille, un fournisseur ou une saison.')}
        />
      )}

      {list.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {drag.order.map((id, rank) => {
              const p = byId.get(id);
              if (!p) return null;
              const state = stateOf(p, now);
              const forme = FORMS.find((f) => f.k === p.form);
              const tone = STATE_TONE[state];
              return (
                <div key={id} {...drag.tile(id)}>
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '15px 17px', flexWrap: 'wrap',
                    border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)',
                  }}>
                    <span aria-hidden {...drag.handle(id)} style={{ paddingTop: 3, display: 'flex', flexShrink: 0 }}>
                      <Icon name="drag" size={15} color="var(--ink-300)" />
                    </span>
                    {/* Le RANG en carré mono — c'est lui que la caisse suit (« la première qui correspond »). */}
                    <span style={{
                      width: 24, height: 24, borderRadius: 'var(--r-xs)', background: 'var(--ink-100)',
                      color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{rank + 1}</span>

                    <span style={{ flex: 1, minWidth: 170 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{p.title}</span>
                        <span style={{
                          height: 21, padding: '0 9px', borderRadius: 'var(--r-pill)', display: 'inline-flex',
                          alignItems: 'center', background: tone.bg, color: tone.fg, fontSize: 'var(--t-meta)', fontWeight: 700,
                        }}>{t(STATE_LABEL[state])}</span>
                      </span>
                      <span style={{ display: 'block', fontSize: 'var(--t-body)', color: 'var(--ink-600)', marginTop: 4 }}>
                        {p.targetDim === null ? (
                          // ⚠️ SANS CIBLE, LA PROMOTION NE REMISE RIEN — et le cœur en fait la règle
                          //    (« cible vide = aucun article »). C'est donc une anomalie, pas un détail.
                          <strong style={{ color: 'var(--rose-fg)' }}>{t('Aucune cible — cette promotion ne remise aucun article')}</strong>
                        ) : (
                          <>
                            {p.targetDim === 'tout'
                              ? <strong style={{ color: 'var(--ink-800)' }}>{t('Toute la boutique')}</strong>
                              : <>{isTargetDim(p.targetDim) ? dimLabel(p.targetDim) : p.targetDim} <strong style={{ color: 'var(--ink-800)' }}>{p.targetValue}</strong></>}
                            {' · '}<Trans k='<1>{n}</1> article concerné' vars={{ n: p.articleCount }}
                              parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} />
                            {p.extraTargets > 0 && ` · ${t('+{n} autre ciblage', { n: p.extraTargets })}`}
                            {p.exclusionCount > 0 && ` · ${t('{n} exclusion', { n: p.exclusionCount })}`}
                          </>
                        )}
                      </span>
                      <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 3 }}>
                        {toFrenchDate(p.startsAt)} → {toFrenchDate(p.endsAt)} · {t(COMBINE_LABEL[p.combineMode ?? ''] ?? key('Politique du magasin'))}
                      </span>
                    </span>

                    <span style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ display: 'block', fontSize: 'var(--t-h3)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>{readableValue(p)}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 2 }}>
                        {forme ? t(forme.l) : null}
                      </span>
                    </span>

                    <span style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
                      {/* Le VRAI interrupteur de la maquette — l'état se lit à la position de la bille. */}
                      <button
                        type="button" data-promo-action aria-label={p.isActive ? t('Désactiver « {v} »', { v: p.title }) : t('Activer « {v} »', { v: p.title })}
                        aria-pressed={p.isActive}
                        style={{ border: 'none', background: 'transparent', padding: 6, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => void agir(async () => {
                          await api.setPromotionActive(p.id, !p.isActive);
                          return p.isActive ? t('« {title} » désactivée', { title: p.title }) : t('« {title} » activée', { title: p.title });
                        })}
                      >
                        <span style={{
                          width: 38, height: 22, borderRadius: 'var(--r-pill)',
                          background: p.isActive ? 'var(--teal-500)' : 'var(--ink-200)',
                          display: 'flex', alignItems: 'center',
                          justifyContent: p.isActive ? 'flex-end' : 'flex-start',
                          padding: '0 3px', flexShrink: 0, transition: 'background .12s',
                        }}>
                          <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} />
                        </span>
                      </button>
                      <button
                        type="button" data-promo-action aria-label={t('Modifier « {v} »', { v: p.title })} className="btn-ghost"
                        onClick={() => setForm({
                          id: p.id, title: p.title, form: p.form,
                          // ⚠️ L'UNITÉ SUIT LA FORME, À LA RELECTURE AUSSI : un montant revient en CENTIMES — remis tel quel dans le
                          //    champ, « 500 » (5 €) repartait multiplié par cent à l'enregistrement.
                          value: p.form === 'amount' ? toInputValue(p.value) : formatRateBp(p.value, false),
                          buyQty: String(p.buyQty || 3), discountQty: String(p.discountQty || 1),
                          from: toIsoDate(p.startsAt), to: toIsoDate(p.endsAt),
                          combineMode: p.combineMode ?? 'best',
                          targetDim: p.targetDim && isTargetDim(p.targetDim) ? p.targetDim : 'fam',
                          targetValue: p.targetValue ?? '',
                        })}
                      ><Icon name="edit" size={15} /></button>
                      <button
                        type="button" data-promo-action aria-label={t('Supprimer « {v} »', { v: p.title })} className="btn-ghost"
                        onClick={() => void (async () => {
                          const ok = await confirm({
                            title: t('Supprimer « {title} » ?', { title: p.title }),
                            // ⚠️ LA PHRASE DE LA MAQUETTE, et elle est VRAIE par construction : une pièce
                            //    fiscale est scellée, elle ne se recalcule pas.
                            message: t('Les ventes déjà réalisées sous cette promotion gardent leur prix — un prix appliqué ne se recalcule jamais. Seules les ventes à venir sont concernées.'),
                            confirmLabel: t('Supprimer'), tone: 'danger',
                          });
                          if (!ok) return;
                          await agir(async () => { await api.deletePromotion(p.id); return t('« {title} » supprimée', { title: p.title }); });
                        })()}
                      ><Icon name="trash" size={15} /></button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Le bandeau AMBRE de la maquette — pas une note grise : c'est la règle qui décide du
              prix encaissé, elle doit se voir. */}
          <div style={{
            marginTop: 14, padding: '14px 16px', borderRadius: 'var(--r-sm)',
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
            display: 'flex', gap: 12,
          }}>
            <span style={{ color: 'var(--amber-fg)', flexShrink: 0, display: 'flex', paddingTop: 1 }}>
              <Icon name="info" size={18} />
            </span>
            <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.6 }}>
              <Trans k='<1>L’ordre décide.</1> Quand deux promotions visent le même article, la caisse retient la première de cette liste — glissez pour changer la priorité. Le réglage <2>« au plus favorable »</2> est le défaut : à égalité de rang, c’est la remise la plus avantageuse pour le client qui s’applique, jamais la plus rentable pour le magasin.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
          </div>
        </>
      )}

      {form && (
        <PromotionForm
          brouillon={form}
          occupe={occupe}
          onClose={() => setForm(null)}
          onSave={(b, value) => void agir(async () => {
            await api.savePromotion(toPayload(b, value), `promo:save:${b.id ?? 'new'}:${Date.now()}`);
            setForm(null);
            return b.id ? t('« {v} » enregistrée', { v: b.title.trim() }) : t('« {v} » créée', { v: b.title.trim() });
          })}
        />
      )}
      </>)}
    </div>
  );
}

/**
 * La valeur saisie, dans l'unité de sa forme — `null` si elle n'en est pas une, ou si elle vaut zéro.
 * ⚠️ PAR LES PORTES UNIQUES : `parseMinor` à l'échelle de la devise pour un montant, `parseRateBp` pour un taux. La
 *    lecture écrite ici multipliait par cent au flottant — elle ARRONDISSAIT une décimale de trop en silence, et dans
 *    un magasin en yens elle aurait écrit cent fois trop.
 */
function valueOf(b: Draft): number | null {
  if (b.form === 'amount') {
    const lu = parseMinor(b.value);
    return lu.state === 'ok' && lu.minor > 0 ? lu.minor : null;
  }
  const bp = parseRateBp(b.value);
  return bp !== null && bp > 0 ? bp : null;
}

/**
 * Le brouillon → ce que le serveur attend.
 * ⚠️ LA VALEUR CHANGE D'UNITÉ AVEC LA FORME : points de base pour un pourcentage, CENTIMES pour des
 * euros. Envoyer « 20 » sans savoir laquelle des deux, c'est écrire 0,20 € ou 20 % au hasard.
 */
function toPayload(b: Draft, value: number): PromotionSave {
  return {
    id: b.id,
    title: b.title.trim(),
    form: b.form,
    value,
    buyQty: Number(b.buyQty) || 0,
    discountQty: Number(b.discountQty) || 0,
    startsAt: fromIsoDate(b.from, 'debut'),
    endsAt: fromIsoDate(b.to, 'fin'),
    combineMode: b.combineMode,
    targetDim: b.targetDim,
    targetValue: b.targetValue.trim(),
  };
}

/** La modale de création / modification — mêmes champs et même ordre que la maquette. */
function PromotionForm({ brouillon, occupe, onClose, onSave }: {
  brouillon: Draft;
  occupe: boolean;
  onClose: () => void;
  /** Le brouillon et sa valeur LUE dans l'unité de sa forme — la lecture se fait une fois, là où elle se juge. */
  onSave: (b: Draft, value: number) => void;
}) {
  const [b, setB] = useState<Draft>(brouillon);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setB((p) => ({ ...p, [k]: v }));
  // ⚠️ LES LISTES D'OUVERTURE SE CHARGENT, ELLES NE S'ATTENDENT PAS. Le picker ne parlait qu'à la
  //    route de RECHERCHE, qui rend délibérément une liste vide tant qu'aucun terme n'est frappé :
  //    l'opérateur ouvrait donc un sélecteur vide sans savoir qu'il devait taper à l'aveugle le nom
  //    d'une valeur qu'il venait justement chercher. Les quatre autres écrans qui ciblent par facette
  //    préchargent — celui-ci ne le faisait pas.
  const [facettes, setFacettes] = useState<ArticleFacets | null>(null);
  const [facettesKo, setFacettesKo] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    api.articleFacets()
      .then((f) => { if (vivant) { setFacettes(f); setFacettesKo(null); } })
      // ⚠️ UN ÉCHEC NE REND PAS UNE LISTE VIDE : elle se lirait « ce magasin n'a aucune marque ». Le
      //    picker reste inerte et DIT pourquoi (§23.0).
      .catch((e: unknown) => { if (vivant) { setFacettes(null); setFacettesKo(e instanceof Error ? e.message : String(e)); } });
    return () => { vivant = false; };
  }, []);
  const ouverture = facettes?.values[b.targetDim as ArticleFacet] ?? [];
  const value = valueOf(b);
  const valueOk = value !== null;
  const ready = !!b.title.trim() && !!b.targetValue.trim() && !!b.from && !!b.to && valueOk;

  return (
    // ⚠️ LA PRIMITIVE PARTAGÉE, PLUS UN VOILE FABRIQUÉ ICI. Celui d'avant peignait `var(--scrim)` BRUT,
    //    or ce jeton est une ENCRE OPAQUE (#0c0e10) que la modale partagée DILUE — la page disparaissait
    //    donc entièrement derrière un aplat noir. Elle apporte aussi le portail (le panneau échappe à
    //    tout ancêtre qui rognerait), la fermeture par Échap, le retour du focus et le plan de surcouche.
    <Modal
      open
      onClose={onClose}
      blockClose={() => occupe}
      title={b.id ? t('Modifier la promotion') : t('Nouvelle promotion')}
      icon="tag"
      tone="teal"
      width="md"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          {/* ⚠️ INACTIF TANT QUE LA RÈGLE EST INCOMPLÈTE, et le survol dit ce qui manque : un bouton
              qui part et revient en erreur fait recommencer la saisie pour rien. */}
          <button
            type="button" className="btn-cta"
            aria-disabled={!ready || occupe || undefined}
            title={ready ? undefined : t('Il manque le nom, la valeur ciblée, la remise ou les dates.')}
            onClick={() => { if (ready && value !== null && !occupe) onSave(b, value); }}
          >{t('Enregistrer')}</button>
        </>
      }
    >
      <>
        <FormRow label={t('Nom de la promotion')}>
          <input
            value={b.title} onChange={(e) => set('title', e.target.value)}
            placeholder={t('-20 % sur les mailles')} style={fieldStyle}
          />
        </FormRow>

        <FormRow label={t('Type de remise')}>
          <PillChoice options={FORMS.map((f) => ({ k: f.k, l: t(f.l) }))} valeur={b.form} onPick={(k) => set('form', k)} />
        </FormRow>

        <div style={{ display: 'grid', gridTemplateColumns: b.form === 'xy' ? '1fr 1fr 1fr' : '1fr', gap: 12 }}>
          {b.form === 'xy' && (
            <>
              <FormRow label={t('Articles achetés')}>
                <input value={b.buyQty} onChange={(e) => set('buyQty', e.target.value.replace(/\D/g, ''))} style={fieldStyle} />
              </FormRow>
              <FormRow label={t('Articles remisés')}>
                <input value={b.discountQty} onChange={(e) => set('discountQty', e.target.value.replace(/\D/g, ''))} style={fieldStyle} />
              </FormRow>
            </>
          )}
          <FormRow label={t('Remise — {v}', { v: b.form === 'amount' ? currentCurrency().symbol : '%' })}>
            <input value={b.value} onChange={(e) => set('value', e.target.value)} style={fieldStyle} />
          </FormRow>
        </div>

        <FormRow label={t('Cibler par')}>
          <PillChoice options={DIMS.map((k) => ({ k, l: dimLabel(k) }))} valeur={b.targetDim} onPick={(k) => setB((p) => ({ ...p, targetDim: k, targetValue: '' }))} />
        </FormRow>

        <FormRow label={t('Valeur ciblée')}>
          {/* ⚠️ LES VALEURS VIENNENT DU SERVEUR, pas d'une saisie libre : une faute de frappe ciblerait
              une famille qui n'existe pas, et la promotion ne remiserait rien sans qu'aucune erreur
              ne le dise. La recherche est celle des facettes d'articles — donc la MÊME source que
              les filtres du catalogue. */}
          <Picker
            id="bo-promo-target"
            variant="field-inline"
            label={t('Valeur ciblée')}
            allLabel={t('À choisir')}
            // ⚠️ LA VALEUR DÉJÀ POSÉE RESTE EN TÊTE même si elle ne figure pas dans la liste d'ouverture
            //    (elle est tronquée à N valeurs) : sans elle, rouvrir une promotion existante afficherait
            //    un champ vide à la place de sa cible.
            options={[
              ...(b.targetValue && !ouverture.some((o) => o.value === b.targetValue)
                ? [{ value: b.targetValue, label: b.targetValue }] : []),
              ...ouverture.map((o) => ({ value: o.value, label: o.value })),
            ]}
            onSearch={(term) => api.articleFacetSearch(b.targetDim as ArticleFacet, term).then((r) => r.map((o) => ({ value: o.value, label: o.value })))}
            value={b.targetValue}
            onChange={(v) => set('targetValue', v)}
          />
                  {/* ⚠️ UN CHARGEMENT RATÉ SE DIT, il ne se lit pas comme un magasin sans marques (§23.0). */}
          {facettesKo && (
            <span style={{ display: 'block', marginTop: 5, fontSize: 'var(--t-note)', color: 'var(--danger-ink)', lineHeight: 1.5 }}>
              {t('Les valeurs n’ont pas pu être chargées ({facettesKo}) — la recherche par frappe reste possible.', { facettesKo })}</span>
          )}
          {/* ⚠️ LE GROUPE EST DÉPLIÉ EN MARQUES À L'ENREGISTREMENT (la caisse compare la marque de l'article) :
              une marque rattachée ensuite n'est pas couverte, et ça se dit avant d'enregistrer. */}
          {b.targetDim === 'sup' && (
            <span style={{ display: 'block', marginTop: 5, fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              {t('Le groupe couvre les marques qui lui sont rattachées au moment de l’enregistrement. Une marque rattachée plus tard demande de réenregistrer la promotion.')}</span>
          )}
</FormRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label={t('Du')}>
            <input type="date" value={b.from} onChange={(e) => set('from', e.target.value)} style={fieldStyle} />
          </FormRow>
          <FormRow label={t('Au')}>
            <input type="date" value={b.to} onChange={(e) => set('to', e.target.value)} style={fieldStyle} />
          </FormRow>
        </div>

        <FormRow
          label={t('Cumul avec les autres remises')}
          aide={t('« Au plus favorable » retient la remise la plus avantageuse pour le client. « Non cumulable » fige le prix promo : ni remise de ligne, ni remise globale, ni fidélité.')}
        >
          <PillChoice options={COMBINE_MODES.map((c) => ({ k: c.k, l: t(c.l) }))} valeur={b.combineMode} onPick={(k) => set('combineMode', k)} />
        </FormRow>

      </>
    </Modal>
  );
}

const fieldStyle = {
  width: '100%', height: 36, padding: '0 10px', border: '1px solid var(--ink-200)',
  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)',
  fontSize: '13px', outline: 'none',
} as const;

function FormRow({ label, aide, children }: { label: string; aide?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 }}>{label}</span>
      {children}
      {aide && <span style={{ display: 'block', marginTop: 5, fontSize: 'var(--t-note)', color: 'var(--ink-400)', lineHeight: 1.5 }}>{aide}</span>}
    </label>
  );
}

/** Un choix unique en pastilles — le contrôle de la maquette pour les listes courtes et fermées. */
function PillChoice<T extends string>({ options, valeur, onPick }: {
  options: readonly { k: T; l: string }[];
  valeur: string;
  onPick: (k: T) => void;
}) {
  return (
    <span style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const on = valeur === o.k;
        return (
          <button
            key={o.k} type="button" onClick={() => onPick(o.k)}
            style={{
              height: 32, padding: '0 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
              border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
              background: on ? 'var(--teal-100)' : 'var(--paper)',
              color: on ? 'var(--teal-800)' : 'var(--ink-600)',
              fontSize: '12.5px', fontWeight: on ? 700 : 500,
            }}
          >{o.l}</button>
        );
      })}
    </span>
  );
}
